/**
 * CallContext - Global state management for WhatsApp Calling with WebRTC
 * 
 * PHASE 3: Full WebRTC + SDP Support with Webhook-Driven State
 * 
 * CRITICAL: UI must reflect REAL state from webhooks:
 * - RINGING: Call is ringing on user's phone
 * - ACCEPTED: User answered (media should start flowing)
 * - CONNECTED: Media confirmed flowing (via WebRTC stats)
 * - FAILED: Error 138021 = no media received
 * 
 * Business-Initiated Call Flow:
 * 1. Agent clicks Call → WebRTC creates offer
 * 2. Offer sent to WhatsApp API
 * 3. Webhook: "connect" with SDP ANSWER
 * 4. Apply answer → ICE/DTLS completes
 * 5. Webhook: "RINGING"
 * 6. Webhook: "ACCEPTED" → Media MUST flow now
 * 7. WebRTC stats confirm bytesSent increasing
 */

import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { createWebRTCService } from './services/WebRTCService';
import { callSounds } from './services/CallSounds';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress } from '@mui/material';
import { MdWarning } from 'react-icons/md';
import toast from 'react-hot-toast';

export const CallContext = createContext(null);

// API base URL
const API_BASE = process.env.REACT_APP_API_URL || '';

// Call status constants - matches backend
export const CallStatus = {
  INITIATED: 'initiated',
  RINGING: 'ringing',
  ACCEPTED: 'accepted',
  CONNECTED: 'connected',
  ENDED: 'ended',
  FAILED: 'failed',
};

export const CallDirection = {
  INBOUND: 'inbound',
  OUTBOUND: 'outbound',
};

function CallProvider({ children }) {
  // State
  const [activeCalls, setActiveCalls] = useState([]);
  const [currentCall, setCurrentCall] = useState(null);
  const [agentStatus, setAgentStatus] = useState('available');
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [showIncomingCall, setShowIncomingCall] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [connectionState, setConnectionState] = useState('closed');
  const [mediaFlowing, setMediaFlowing] = useState(false);  // Track actual RTP flow

  // Permission dialog state
  const [permissionDialog, setPermissionDialog] = useState({
    open: false,
    phoneNumber: '',
    businessNumber: '',
    isSending: false
  });

  // Refs
  const eventSourceRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const webRTCRef = useRef(null);
  const remoteAudioRef = useRef(null);

  // Recording refs
  const mediaRecorderRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const isRecordingRef = useRef(false);

  // ============================================
  // WebRTC Handlers
  // ============================================

  /**
   * Handle WebRTC connection state changes
   */
  const handleWebRTCStateChange = useCallback((state) => {
    console.log('[CallContext] WebRTC connection state:', state);
    setConnectionState(state);

    if (state === 'connected') {
      console.log('[CallContext] WebRTC connected - waiting for media confirmation');
    } else if (state === 'failed') {
      console.error('[CallContext] WebRTC connection FAILED');
      setError('WebRTC connection failed - check network');
      setMediaFlowing(false);
    } else if (state === 'disconnected' || state === 'closed') {
      setMediaFlowing(false);
    }
  }, []);

  /**
   * Handle media flow confirmation (RTP packets actually being sent)
   */
  const handleMediaFlowing = useCallback((flowing) => {
    console.log('[CallContext] *** MEDIA FLOWING:', flowing, '***');
    setMediaFlowing(flowing);

    if (flowing) {
      // Media confirmed - update agent status
      setAgentStatus('busy');

      // Start recording after a short delay to ensure streams are ready
      setTimeout(() => {
        if (!isRecordingRef.current && webRTCRef.current) {
          // Start recording
          try {
            const webrtc = webRTCRef.current;
            if (!webrtc || !webrtc.peerConnection) {
              console.log('[CallContext] No WebRTC connection for recording');
              return;
            }

            // Create AudioContext to mix local and remote audio
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const destination = audioContext.createMediaStreamDestination();

            // Add local audio (from microphone)
            const localStream = webrtc.localStream;
            if (localStream) {
              const localSource = audioContext.createMediaStreamSource(localStream);
              localSource.connect(destination);
            }

            // Add remote audio
            const remoteStream = webrtc.remoteStream;
            if (remoteStream) {
              const remoteSource = audioContext.createMediaStreamSource(remoteStream);
              remoteSource.connect(destination);
            }

            // Create MediaRecorder with mixed stream
            const mixedStream = destination.stream;
            const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
              ? 'audio/webm;codecs=opus'
              : 'audio/webm';

            const recorder = new MediaRecorder(mixedStream, { mimeType });
            recordingChunksRef.current = [];

            recorder.ondataavailable = (event) => {
              if (event.data.size > 0) {
                recordingChunksRef.current.push(event.data);
              }
            };

            recorder.start(1000); // Collect data every second
            mediaRecorderRef.current = recorder;
            isRecordingRef.current = true;

            console.log('[CallContext] Call recording started');
          } catch (e) {
            console.error('[CallContext] Failed to start recording:', e);
          }
        }
      }, 500);
    }
  }, []);

  /**
   * Handle remote audio stream
   */
  const handleRemoteStream = useCallback((stream) => {
    console.log('[CallContext] Remote audio stream received');

    // Create or get audio element for remote audio
    if (!remoteAudioRef.current) {
      remoteAudioRef.current = new Audio();
      remoteAudioRef.current.autoplay = true;
    }
    remoteAudioRef.current.srcObject = stream;
  }, []);

  /**
   * Initialize WebRTC for a call
   */
  const initWebRTC = useCallback(async () => {
    console.log('[CallContext] Initializing WebRTC');

    // Create new WebRTC service
    const webrtc = createWebRTCService();
    webRTCRef.current = webrtc;

    // Set up callbacks
    webrtc.onStateChange = handleWebRTCStateChange;
    webrtc.onRemoteStream = handleRemoteStream;
    webrtc.onMediaFlowing = handleMediaFlowing;  // Track actual RTP

    // Initialize peer connection
    await webrtc.init();

    // Get microphone access - MUST happen BEFORE createOffer
    await webrtc.getUserMedia();

    return webrtc;
  }, [handleWebRTCStateChange, handleRemoteStream, handleMediaFlowing]);

  /**
   * Clean up WebRTC resources
   */
  const cleanupWebRTC = useCallback(() => {
    console.log('[CallContext] Cleaning up WebRTC');

    if (webRTCRef.current) {
      webRTCRef.current.close();
      webRTCRef.current = null;
    }

    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }

    setConnectionState('closed');
    setIsMuted(false);
    setMediaFlowing(false);
  }, []);

  /**
   * Start recording the call audio
   */
  const startRecording = useCallback(() => {
    if (isRecordingRef.current) {
      console.log('[CallContext] Already recording');
      return;
    }

    try {
      // Get the local media stream from WebRTC
      const webrtc = webRTCRef.current;
      if (!webrtc || !webrtc.peerConnection) {
        console.log('[CallContext] No WebRTC connection for recording');
        return;
      }

      // Create AudioContext to mix local and remote audio
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const destination = audioContext.createMediaStreamDestination();

      // Add local audio (from microphone)
      const localStream = webrtc.localStream;
      if (localStream) {
        const localSource = audioContext.createMediaStreamSource(localStream);
        localSource.connect(destination);
      }

      // Add remote audio
      const remoteStream = webrtc.remoteStream;
      if (remoteStream) {
        const remoteSource = audioContext.createMediaStreamSource(remoteStream);
        remoteSource.connect(destination);
      }

      // Create MediaRecorder with mixed stream
      const mixedStream = destination.stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const recorder = new MediaRecorder(mixedStream, { mimeType });
      recordingChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };

      recorder.start(1000); // Collect data every second
      mediaRecorderRef.current = recorder;
      isRecordingRef.current = true;

      console.log('[CallContext] Call recording started');
    } catch (e) {
      console.error('[CallContext] Failed to start recording:', e);
    }
  }, []);

  /**
   * Stop recording and upload to server
   */
  const stopRecording = useCallback(async (callId) => {
    if (!isRecordingRef.current || !mediaRecorderRef.current) {
      console.log('[CallContext] No active recording to stop');
      return;
    }

    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;

      recorder.onstop = async () => {
        console.log('[CallContext] Recording stopped, uploading...');
        isRecordingRef.current = false;

        // Create blob from chunks
        const blob = new Blob(recordingChunksRef.current, { type: 'audio/webm' });
        recordingChunksRef.current = [];

        // Only upload if we have data and a call ID
        if (blob.size > 0 && callId) {
          try {
            const formData = new FormData();
            formData.append('recording', blob, `${callId}.webm`);

            await axios.post(`${API_BASE}/api/calls/${callId}/recording/`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });

            console.log('[CallContext] Recording uploaded successfully');
            toast.success('Call recording saved');
          } catch (e) {
            console.error('[CallContext] Failed to upload recording:', e);
          }
        }

        mediaRecorderRef.current = null;
        resolve();
      };

      recorder.stop();
    });
  }, []);

  // ============================================
  // SSE Connection and Polling
  // ============================================

  // Define startPolling FIRST (before connectSSE which uses it)
  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    console.log('[CallContext] Starting polling fallback');

    const poll = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/calls/active/`);
        if (response.data.success) {
          const calls = response.data.calls || [];
          setActiveCalls(calls);
          setIsConnected(true);

          // Sync currentCall with active_calls
          setCurrentCall(prevCall => {
            if (!prevCall) {
              // Look for new incoming call
              const incomingCall = calls.find(
                c => c.direction === CallDirection.INBOUND &&
                  c.status === CallStatus.RINGING &&
                  !c.agent_id
              );
              if (incomingCall) {
                setShowIncomingCall(true);
                return incomingCall;
              }
              return null;
            }

            // Update current call with latest from polling
            const updated = calls.find(c => c.call_id === prevCall.call_id);
            if (!updated) {
              // Call ended
              if (webRTCRef.current) {
                webRTCRef.current.close();
                webRTCRef.current = null;
              }
              setAgentStatus('available');
              return null;
            }
            return updated;
          });
        }
      } catch (e) {
        console.error('[CallContext] Polling error:', e);
        setIsConnected(false);
      }
    };

    poll();
    pollIntervalRef.current = setInterval(poll, 3000);
  }, []);

  // Now define connectSSE (can use startPolling since it's defined above)
  const connectSSE = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      console.log('[CallContext] Connecting to SSE...');
      const eventSource = new EventSource(`${API_BASE}/api/calls/events/`);

      eventSource.onopen = () => {
        console.log('[CallContext] SSE connected successfully');
        setIsConnected(true);
        setError(null);
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Log all events for debugging
          if (data.type !== 'heartbeat') {
            console.log('[CallContext] SSE EVENT:', data.type, data.call_id || '', data.status || '');
          }

          if (data.type === 'heartbeat') {
            const calls = data.active_calls || [];
            setActiveCalls(calls);

            // Sync currentCall with active_calls using functional update
            setCurrentCall(prevCall => {
              if (!prevCall) {
                // Look for new incoming call
                const incomingCall = calls.find(
                  c => c.direction === CallDirection.INBOUND &&
                    c.status === CallStatus.RINGING &&
                    !c.agent_id
                );
                if (incomingCall) {
                  console.log('[CallContext] New incoming call:', incomingCall.call_id);
                  setShowIncomingCall(true);
                  return incomingCall;
                }
                return null;
              }

              // Find matching call in active_calls
              const updatedCall = calls.find(c => c.call_id === prevCall.call_id);

              if (!updatedCall) {
                // Call ended
                console.log('[CallContext] Call ended (removed from active):', prevCall.call_id);
                if (webRTCRef.current) {
                  webRTCRef.current.close();
                  webRTCRef.current = null;
                }
                setShowIncomingCall(false);
                setAgentStatus('available');
                setConnectionState('closed');
                setMediaFlowing(false);
                return null;
              }

              // Log status change
              if (updatedCall.status !== prevCall.status) {
                console.log('[CallContext] *** STATUS CHANGE:', prevCall.status, '->', updatedCall.status);
              }

              return updatedCall;
            });

          } else if (data.type === 'call_update') {
            console.log('[CallContext] *** CALL UPDATE:', data.call_id, data.call?.status);

            const newCall = data.call || {};

            // Handle INCOMING calls - set as current call even if no prevCall
            if (!newCall.call_id) {
              console.log('[CallContext] Ignored update: no call_id in payload');
              return;
            }

            // Check if this is an incoming call (user initiated)
            const isIncoming = newCall.direction === 'inbound';

            // Update currentCall
            setCurrentCall(prevCall => {
              // CASE 1: No existing call
              if (!prevCall) {
                // For INCOMING calls, accept them as new current call
                if (isIncoming && newCall.is_active) {
                  console.log('[CallContext] Accepting INCOMING call:', newCall.call_id);
                  setShowIncomingCall(true);  // Show incoming call UI
                  return newCall;
                }
                // For outbound updates with no prevCall, ignore (shouldn't happen)
                console.log('[CallContext] Ignored update: prevCall is null (outbound)');
                return prevCall;
              }

              // CASE 2: Existing call - check if update matches
              const matchesCurrent = prevCall.call_id === newCall.call_id;
              const matchesOriginal = newCall.metadata?.original_call_id === prevCall.call_id;

              if (!matchesCurrent && !matchesOriginal) {
                // Check if this is a different incoming call
                if (isIncoming && newCall.is_active && !prevCall.is_active) {
                  console.log('[CallContext] New INCOMING call replacing ended call:', newCall.call_id);
                  setShowIncomingCall(true);
                  return newCall;
                }
                console.log('[CallContext] Ignored update: Call ID Mismatch',
                  prevCall.call_id, 'vs update', newCall.call_id,
                  '(Original matches:', matchesOriginal, ')'
                );
                return prevCall;
              }

              if (matchesOriginal) {
                console.log('[CallContext] Call ID evolved:', prevCall.call_id, '->', newCall.call_id);
              }

              // SDP RECOVERY (Crucial for race conditions)
              // Diagnostic logging
              console.log('[CallContext] SDP Recovery Check:', {
                hasRemoteSdp: !!newCall.remote_sdp,
                hasWebRTC: !!webRTCRef.current,
                signalingState: webRTCRef.current?.signalingState,
                hasRemoteDesc: !!webRTCRef.current?.remoteDescription
              });

              if (newCall.remote_sdp && webRTCRef.current) {
                const service = webRTCRef.current;
                if (service.signalingState === 'have-local-offer' && !service.remoteDescription) {
                  console.log('[CallContext] Recovering SDP Answer from call_update payload');
                  // Use the service's setRemoteAnswer method, not direct peer connection access
                  service.setRemoteAnswer(newCall.remote_sdp)
                    .then(() => {
                      console.log('[CallContext] Recovery SDP applied successfully');
                    })
                    .catch(e => console.error('[CallContext] Failed to apply recovery SDP:', e));
                } else {
                  console.log('[CallContext] SDP Recovery skipped:',
                    'signalingState=' + service.signalingState,
                    'hasRemoteDesc=' + !!service.remoteDescription
                  );
                }
              }

              if (!newCall.is_active) {
                console.log('[CallContext] Call ended via update');
                if (webRTCRef.current) {
                  webRTCRef.current.close();
                  webRTCRef.current = null;
                }
                setShowIncomingCall(false);
                setAgentStatus('available');
                setConnectionState('closed');
                setMediaFlowing(false);
                return null;
              }

              return newCall;
            });

          } else if (data.type === 'sdp_answer') {
            console.log('[CallContext] *** SDP ANSWER RECEIVED:', data.call_id);

            if (webRTCRef.current && data.sdp) {
              webRTCRef.current.setRemoteAnswer(data.sdp)
                .then(() => console.log('[CallContext] *** SDP ANSWER APPLIED ***'))
                .catch(err => {
                  console.error('[CallContext] SDP apply failed:', err);
                  setError('SDP failed: ' + err.message);
                });
            } else {
              console.error('[CallContext] No WebRTC or SDP empty');
            }
          }
        } catch (e) {
          console.error('[CallContext] SSE parse error:', e);
        }
      };

      eventSource.onerror = (e) => {
        console.error('[CallContext] SSE error, falling back to polling:', e);
        setIsConnected(false);
        eventSource.close();
        // Fall back to polling
        startPolling();
      };

      eventSourceRef.current = eventSource;
    } catch (e) {
      console.error('[CallContext] EventSource creation failed:', e);
      startPolling();
    }
  }, [startPolling]);

  // ============================================
  // Call Actions with WebRTC
  // ============================================

  /**
   * Accept an incoming call with SDP
   * @param {string} callId 
   * @param {object} agentInfo 
   */
  const acceptCall = useCallback(async (callId, agentInfo = {}) => {
    try {
      setError(null);
      console.log('[CallContext] Accepting call:', callId);

      // Get the call's remote SDP (offer from WhatsApp)
      const call = activeCalls.find(c => c.call_id === callId) || currentCall;
      const remoteSdp = call?.metadata?.session_sdp || call?.remote_sdp;

      if (!remoteSdp) {
        console.error('[CallContext] No SDP offer found for call');
        setError('No SDP offer available - call may have expired');
        return { success: false, error: 'No SDP offer available' };
      }

      // Initialize WebRTC and create answer
      const webrtc = await initWebRTC();
      const localSdp = await webrtc.createAnswer(remoteSdp);

      console.log('[CallContext] SDP answer created, sending to backend');

      // Send pre_accept with SDP to backend
      const preAcceptResponse = await axios.post(`${API_BASE}/api/calls/${callId}/pre-accept/`, {
        agent_id: agentInfo.agent_id || 'web-agent',
        agent_name: agentInfo.agent_name || 'Web Agent',
        sdp: localSdp,
      });

      if (!preAcceptResponse.data.success) {
        throw new Error(preAcceptResponse.data.error || 'Pre-accept failed');
      }

      console.log('[CallContext] Pre-accept successful, sending accept');

      // Send accept with final SDP
      const acceptResponse = await axios.post(`${API_BASE}/api/calls/${callId}/accept/`, {
        agent_id: agentInfo.agent_id || 'web-agent',
        agent_name: agentInfo.agent_name || 'Web Agent',
        sdp: localSdp,
      });

      if (acceptResponse.data.success) {
        setCurrentCall(acceptResponse.data.call);
        setShowIncomingCall(false);
        setAgentStatus('busy');
        return { success: true, call: acceptResponse.data.call };
      } else {
        cleanupWebRTC();
        setError(acceptResponse.data.error || 'Failed to accept call');
        return { success: false, error: acceptResponse.data.error };
      }
    } catch (e) {
      cleanupWebRTC();
      const errorMsg = e.response?.data?.error || e.message;
      setError(errorMsg);
      console.error('[CallContext] Accept call error:', e);
      return { success: false, error: errorMsg };
    }
  }, [activeCalls, currentCall, initWebRTC, cleanupWebRTC]);

  /**
   * Reject an incoming call
   */
  const rejectCall = useCallback(async (callId, reason = 'agent_rejected') => {
    try {
      setError(null);
      const response = await axios.post(`${API_BASE}/api/calls/${callId}/reject/`, {
        reason,
      });

      if (response.data.success) {
        setCurrentCall(null);
        setShowIncomingCall(false);
        setAgentStatus('available');
        return { success: true };
      } else {
        setError(response.data.error || 'Failed to reject call');
        return { success: false, error: response.data.error };
      }
    } catch (e) {
      const errorMsg = e.response?.data?.error || e.message;
      setError(errorMsg);
      console.error('[CallContext] Reject call error:', e);
      return { success: false, error: errorMsg };
    }
  }, []);

  /**
   * End an active call
   */
  const endCall = useCallback(async (callId) => {
    try {
      setError(null);

      // Stop any ringing sounds
      callSounds.stop();

      // Stop recording first and upload
      await stopRecording(callId);

      // Cleanup WebRTC
      cleanupWebRTC();

      const response = await axios.post(`${API_BASE}/api/calls/${callId}/end/`);

      if (response.data.success) {
        // Play ended sound
        callSounds.playEnded();
        setCurrentCall(null);
        setAgentStatus('available');
        return { success: true };
      } else {
        setError(response.data.error || 'Failed to end call');
        return { success: false, error: response.data.error };
      }
    } catch (e) {
      const errorMsg = e.response?.data?.error || e.message;
      setError(errorMsg);
      console.error('[CallContext] End call error:', e);
      return { success: false, error: errorMsg };
    }
  }, [cleanupWebRTC, stopRecording]);

  /**
   * Initiate a business call with SDP offer
   */
  const initiateCall = useCallback(async (phoneNumber, businessNumber, agentInfo = {}) => {
    try {
      setError(null);
      console.log('[CallContext] Initiating call to:', phoneNumber);

      // Initialize WebRTC and create offer
      const webrtc = await initWebRTC();
      const localSdp = await webrtc.createOffer();

      console.log('[CallContext] SDP offer created, sending to backend');

      // Send to backend with SDP
      const response = await axios.post(`${API_BASE}/api/calls/initiate/`, {
        phone_number: phoneNumber,
        business_number: businessNumber,
        agent_id: agentInfo.agent_id || 'web-agent',
        agent_name: agentInfo.agent_name || 'Web Agent',
        sdp: localSdp,
      });

      if (response.data.success) {
        setCurrentCall(response.data.call);
        setAgentStatus('busy');
        // Wait for SDP answer via webhook
        return { success: true, call: response.data.call };
      } else {
        cleanupWebRTC();
        setError(response.data.error || 'Failed to initiate call');
        return { success: false, error: response.data.error, error_code: response.data.error_code };
      }
    } catch (e) {
      cleanupWebRTC();
      const errorMsg = e.response?.data?.error || e.message;
      const errorCode = e.response?.data?.error_code;
      setError(errorMsg);
      console.error('[CallContext] Initiate call error:', e);
      return { success: false, error: errorMsg, error_code: errorCode };
    }
  }, [initWebRTC, cleanupWebRTC]);

  /**
   * Request call permission from user
   */
  const requestCallPermission = useCallback(async (phoneNumber, businessNumber) => {
    try {
      setError(null);
      const response = await axios.post(`${API_BASE}/api/calls/request-permission/`, {
        phone_number: phoneNumber,
        business_number: businessNumber,
      });
      return response.data;
    } catch (e) {
      const errorMsg = e.response?.data?.error || e.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, []);

  /**
   * Show permission dialog - can be called from any component
   */
  const showPermissionDialog = useCallback((phoneNumber, businessNumber) => {
    setPermissionDialog({
      open: true,
      phoneNumber: phoneNumber,
      businessNumber: businessNumber,
      isSending: false
    });
  }, []);

  /**
   * Handle permission dialog cancel
   */
  const handlePermissionDialogCancel = useCallback(() => {
    setPermissionDialog({
      open: false,
      phoneNumber: '',
      businessNumber: '',
      isSending: false
    });
  }, []);

  /**
   * Handle send permission request from dialog
   */
  const handleSendPermissionFromDialog = useCallback(async () => {
    setPermissionDialog(prev => ({ ...prev, isSending: true }));
    try {
      const result = await requestCallPermission(
        permissionDialog.phoneNumber,
        permissionDialog.businessNumber
      );

      if (result.success) {
        toast.success('Permission request sent! Ask user to accept.');
      } else {
        toast.error(result.error || 'Failed to send permission request');
      }
    } catch (error) {
      console.error('Permission request error:', error);
      toast.error(error.message || 'Failed to send permission request');
    } finally {
      setPermissionDialog({
        open: false,
        phoneNumber: '',
        businessNumber: '',
        isSending: false
      });
    }
  }, [permissionDialog.phoneNumber, permissionDialog.businessNumber, requestCallPermission]);

  /**
   * Toggle mute
   */
  const toggleMute = useCallback(() => {
    if (webRTCRef.current) {
      const newMuted = !isMuted;
      webRTCRef.current.setMuted(newMuted);
      setIsMuted(newMuted);
    }
  }, [isMuted]);

  const dismissIncomingCall = useCallback(() => {
    setShowIncomingCall(false);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ============================================
  // Effects
  // ============================================

  useEffect(() => {
    connectSSE();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      cleanupWebRTC();
    };
  }, [connectSSE, cleanupWebRTC]);

  // ============================================
  // Call Sounds Effect
  // ============================================

  useEffect(() => {
    // Initialize audio context on first interaction
    callSounds.init();

    // Incoming call - play incoming ring
    if (showIncomingCall && currentCall?.direction === CallDirection.INBOUND) {
      console.log('[CallContext] Playing incoming call sound');
      callSounds.playIncomingCall();
      return;
    }

    // Outbound call - check for ringing status
    if (currentCall?.direction === CallDirection.OUTBOUND) {
      if (currentCall.status === CallStatus.INITIATED || currentCall.status === CallStatus.RINGING) {
        console.log('[CallContext] Playing outbound ringing sound');
        callSounds.playOutboundRinging();
        return;
      }
    }

    // Call connected - stop ringing sounds
    if (currentCall?.status === CallStatus.CONNECTED ||
      currentCall?.status === CallStatus.ACCEPTED ||
      mediaFlowing) {
      if (callSounds.isPlaying) {
        console.log('[CallContext] Call connected, stopping sounds');
        callSounds.playConnected();
      }
      return;
    }

    // No active call or call ended - stop all sounds
    if (!currentCall || currentCall.status === CallStatus.ENDED || currentCall.status === CallStatus.FAILED) {
      callSounds.stop();
    }

    // Cleanup on unmount
    return () => {
      callSounds.stop();
    };
  }, [showIncomingCall, currentCall, mediaFlowing]);

  // ============================================
  // Context Value
  // ============================================

  const value = {
    // State
    activeCalls,
    currentCall,
    agentStatus,
    isConnected,
    error,
    showIncomingCall,
    isMuted,
    connectionState,
    mediaFlowing,  // Track actual RTP flow

    // Actions
    acceptCall,
    rejectCall,
    endCall,
    initiateCall,
    requestCallPermission,
    toggleMute,
    dismissIncomingCall,
    clearError,
    setAgentStatus,
    showPermissionDialog,

    // Constants
    CallStatus,
    CallDirection,
  };

  return (
    <CallContext.Provider value={value}>
      {children}

      {/* Global Permission Request Dialog */}
      <Dialog
        open={permissionDialog.open}
        onClose={handlePermissionDialogCancel}
        PaperProps={{
          style: {
            backgroundColor: '#ffffff',
            color: '#333333',
            borderRadius: '12px',
            padding: '8px',
            minWidth: '380px'
          }
        }}
      >
        <DialogTitle style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          color: '#ff9800',
          fontWeight: 600
        }}>
          <MdWarning size={28} />
          No Call Permission
        </DialogTitle>
        <DialogContent>
          <p style={{
            margin: '8px 0 16px 0',
            fontSize: '15px',
            lineHeight: 1.5,
            color: '#555555'
          }}>
            This business does not have permission to call this number.
            Would you like to send a permission request to the user?
          </p>
          <p style={{
            margin: 0,
            fontSize: '13px',
            color: '#888888'
          }}>
            Phone: <strong>{permissionDialog.phoneNumber}</strong>
          </p>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px', gap: '12px' }}>
          <Button
            onClick={handlePermissionDialogCancel}
            disabled={permissionDialog.isSending}
            style={{
              color: '#666666',
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendPermissionFromDialog}
            disabled={permissionDialog.isSending}
            variant="contained"
            style={{
              backgroundColor: '#25D366',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              padding: '8px 20px',
              borderRadius: '8px'
            }}
          >
            {permissionDialog.isSending ? (
              <CircularProgress size={20} style={{ color: 'white' }} />
            ) : (
              'Send Permission Request'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </CallContext.Provider>
  );
}

export default CallProvider;
